// src/hooks/useOfflineStorage.js
import { useState, useEffect } from 'react';
import ApiService from '../services/api';

const OFFLINE_STORAGE_KEY = 'offline_orders';
const CUSTOMERS_CACHE_KEY = 'customers_cache';
const PRODUCTS_CACHE_KEY = 'products_cache';
const OFFLINE_CUSTOMERS_KEY = 'offline_customers';

export const useOfflineStorage = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineOrders, setOfflineOrders] = useState([]);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false); // Previene multiple sincronizzazioni

  useEffect(() => {
    // Listener per stato connessione
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Carica ordini offline salvati all'avvio
    loadOfflineOrders();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /**
   * Carica gli ordini offline dal localStorage
   * Aggiorna lo stato locale e il contatore ordini pendenti
   */
  const loadOfflineOrders = () => {
    try {
      // Legge dal localStorage
      const stored = localStorage.getItem(OFFLINE_STORAGE_KEY);
      
      // Parse dei dati o array vuoto se non esistono
      const orders = stored ? JSON.parse(stored) : [];
      
      // Valida la struttura dei dati
      const validOrders = orders.filter(order => {
        return (
          order &&
          typeof order === 'object' &&
          order.id &&
          order.data &&
          typeof order.timestamp === 'number' &&
          typeof order.synced === 'boolean'
        );
      });

      // Se alcuni ordini erano malformati, salva solo quelli validi
      if (validOrders.length !== orders.length) {
        console.warn('Alcuni ordini offline erano malformati, sono stati rimossi');
        localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(validOrders));
      }

      // Ordina per timestamp (più recenti prima)
      validOrders.sort((a, b) => b.timestamp - a.timestamp);

      // Aggiorna stato
      setOfflineOrders(validOrders);
      
      // Calcola ordini pendenti (non sincronizzati)
      const pendingCount = validOrders.filter(order => !order.synced).length;
      setPendingSyncCount(pendingCount);      
      return validOrders;
    } catch (error) {
      console.error('Errore caricamento ordini offline:', error);
      
      // In caso di errore, resetta lo storage
      try {
        localStorage.removeItem(OFFLINE_STORAGE_KEY);
        setOfflineOrders([]);
        setPendingSyncCount(0);
      } catch (resetError) {
        console.error('Errore reset storage offline:', resetError);
      }
      
      return [];
    }
  };

  /**
   * Salva un nuovo ordine offline
   * @param {Object} orderData - Dati dell'ordine da salvare
   * @returns {Object} - Ordine offline creato
   */
  const saveOfflineOrder = (orderData) => {
    try {
      // Crea oggetto ordine offline
      const offlineOrder = {
        id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        data: orderData,
        timestamp: Date.now(),
        synced: false,
        token: localStorage.getItem('token'), // Salva token per sync futuro
        retryCount: 0, // Contatore tentativi sync
        lastSyncAttempt: null
      };

      // Carica ordini esistenti
      const existingOrders = JSON.parse(localStorage.getItem(OFFLINE_STORAGE_KEY) || '[]');
      
      // Aggiungi nuovo ordine
      const updatedOrders = [...existingOrders, offlineOrder];
      
      // Salva nel localStorage
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(updatedOrders));
      
      // Aggiorna stato locale
      setOfflineOrders(updatedOrders);
      setPendingSyncCount(updatedOrders.filter(o => !o.synced).length);      
      return offlineOrder;
    } catch (error) {
      console.error('Errore salvataggio ordine offline:', error);
      throw new Error('Impossibile salvare l\'ordine offline');
    }
  };


  /**
   * Rimuove gli ordini già sincronizzati
   */
  const clearSyncedOrders = () => {
    try {
      const orders = JSON.parse(localStorage.getItem(OFFLINE_STORAGE_KEY) || '[]');
      const pendingOrders = orders.filter(order => !order.synced);
      
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(pendingOrders));
      setOfflineOrders(pendingOrders);
      setPendingSyncCount(pendingOrders.length);
          } catch (error) {
      console.error('Errore pulizia ordini sincronizzati:', error);
    }
  };

  /**
   * Sincronizza tutti gli ordini pendenti
   */
  const syncPendingOrders = async () => {
    if (!isOnline) {
      throw new Error('Connessione non disponibile');
    }

    if (isSyncing) {
      throw new Error('Sincronizzazione già in corso');
    }

    setIsSyncing(true);

    try {
      const orders = JSON.parse(localStorage.getItem(OFFLINE_STORAGE_KEY) || '[]');
      const pendingOrders = orders.filter(order => !order.synced);

      if (pendingOrders.length === 0) {
        return { success: true, syncedCount: 0, errors: [] };
      }

      let syncedCount = 0;
      let errors = [];
      const updatedOrders = [...orders];

      // Sincronizza gli ordini sequenzialmente per evitare problemi
      for (let i = 0; i < pendingOrders.length; i++) {
        const order = pendingOrders[i];
        const orderIndex = updatedOrders.findIndex(o => o.id === order.id);
        
        try {
          
          // Incrementa contatore tentativi
          updatedOrders[orderIndex].retryCount = (order.retryCount || 0) + 1;
          updatedOrders[orderIndex].lastSyncAttempt = Date.now();
          const customerData = await ApiService.createCustomer(order.data.customer);
          const orderPayload = {
            customerId: customerData.customer.id,
            paymentMethod: order.data.paymentMethod,
            discount: order.data.discount || 0,
            items: order.data.items.map(item => ({
              productId: item.productId || item.product?.id,
              quantity: item.quantity || item.quantita,
              unitPrice: item.unitPrice || item.prezzo,
              vatRate: item.vatRate || item.iva
            }))
          };
          
          // Crea l'ordine usando ApiService
          const syncedOrder = await ApiService.createOrder(orderPayload);          
          // Marca come sincronizzato nel array locale
          updatedOrders[orderIndex] = {
            ...updatedOrders[orderIndex],
            synced: true,
            syncedAt: Date.now(),
            serverOrderId: syncedOrder.id
          };
          
          syncedCount++;
          
          // Prova a finalizzare l'ordine usando ApiService
          try {
            await ApiService.finalizeOrder(syncedOrder.id);
          } catch (finalizeError) {
            console.warn(`⚠️ Errore finalizzazione ordine ${syncedOrder.id}:`, finalizeError);
          }
        } catch (syncError) {
          console.error(`❌ Errore sincronizzazione ordine ${order.id}:`, syncError);
          const errorMsg = syncError.message || 'Errore sconosciuto';
          
          errors.push({
            orderId: order.id,
            error: errorMsg,
            retryCount: updatedOrders[orderIndex].retryCount
          });
          
          // Se troppi tentativi falliti, marca come errore permanente
          if (updatedOrders[orderIndex].retryCount >= 3) {
            updatedOrders[orderIndex].syncError = errorMsg;
            updatedOrders[orderIndex].permanentFailure = true;
            console.error(`💀 Ordine fallito definitivamente dopo 3 tentativi: ${order.id}`);
          }
        }
      }

      // Salva gli ordini aggiornati UNA SOLA VOLTA alla fine
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(updatedOrders));
      
      // Aggiorna lo stato locale UNA SOLA VOLTA
      setOfflineOrders(updatedOrders);
      const newPendingCount = updatedOrders.filter(o => !o.synced).length;
      setPendingSyncCount(newPendingCount);
      
      const result = {
        success: errors.length < pendingOrders.length,
        syncedCount,
        totalCount: pendingOrders.length,
        errors
      };
      
      return result;
      
    } catch (error) {
      console.error('💥 Errore generale sync ordini pendenti:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  // Cache management per clienti e prodotti
  const cacheCustomers = (customers) => {
    try {
      localStorage.setItem(CUSTOMERS_CACHE_KEY, JSON.stringify({
        data: customers,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Errore cache clienti:', error);
    }
  };

  const getCachedCustomers = () => {
    try {
      const cached = localStorage.getItem(CUSTOMERS_CACHE_KEY);
      if (cached) {
        const { data } = JSON.parse(cached);
        return data || [];
      }
      return [];
    } catch (error) {
      console.error('Errore lettura cache clienti:', error);
      return [];
    }
  };

  const cacheProducts = (products) => {
    try {
      localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify({
        data: products,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Errore cache prodotti:', error);
    }
  };

  const getCachedProducts = () => {
    try {
      const cached = localStorage.getItem(PRODUCTS_CACHE_KEY);
      if (cached) {
        const { data } = JSON.parse(cached);
        return data || [];
      }
      return [];
    } catch (error) {
      console.error('Errore lettura cache prodotti:', error);
      return [];
    }
  };

  // Metodo per controllare se la cache va aggiornata (solo quando online)
  const shouldUpdateCache = () => {
    if (!isOnline) return false; // Non controllare se offline
    
    try {
      const now = Date.now();
      const maxAge = 24 * 60 * 60 * 1000; // 24 ore
      
      // Controlla timestamp cache prodotti
      const productsCache = localStorage.getItem(PRODUCTS_CACHE_KEY);
      const customersCache = localStorage.getItem(CUSTOMERS_CACHE_KEY);
      
      let shouldUpdate = false;
      
      if (productsCache) {
        const { timestamp } = JSON.parse(productsCache);
        if (now - timestamp > maxAge) {
          shouldUpdate = true;
        }
      } else {
        shouldUpdate = true; // Cache vuota
      }
      
      if (customersCache) {
        const { timestamp } = JSON.parse(customersCache);
        if (now - timestamp > maxAge) {
          shouldUpdate = true;
        }
      } else {
        shouldUpdate = true; // Cache vuota
      }
      
      return shouldUpdate;
    } catch (error) {
      console.error('Errore controllo cache:', error);
      return true; // In caso di errore, forza aggiornamento
    }
  };

  // =================== GESTIONE OFFLINE CLIENTI ===================
  
  /**
   * Salva una operazione offline sui clienti (create/update/delete)
   * @param {string} operation - Tipo operazione: 'create', 'update', 'delete'
   * @param {Object} customerData - Dati del cliente
   * @param {string} customerId - ID del cliente (per update/delete)
   * @returns {Object} - Operazione offline creata
   */
  const saveOfflineCustomerOperation = (operation, customerData, customerId = null) => {
    try {
      const offlineOperation = {
        id: `offline_customer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        operation, // 'create', 'update', 'delete'
        customerData,
        customerId, // Per operazioni update/delete
        timestamp: Date.now(),
        synced: false,
        token: localStorage.getItem('token'),
        retryCount: 0,
        lastSyncAttempt: null
      };

      // Carica operazioni esistenti
      const existingOperations = JSON.parse(localStorage.getItem(OFFLINE_CUSTOMERS_KEY) || '[]');
      
      // Aggiungi nuova operazione
      const updatedOperations = [...existingOperations, offlineOperation];
      
      // Salva nel localStorage
      localStorage.setItem(OFFLINE_CUSTOMERS_KEY, JSON.stringify(updatedOperations));
      
      // Se è una creazione, aggiorna anche la cache locale per renderla disponibile subito
      if (operation === 'create') {
        const cachedCustomers = getCachedCustomers();
        const newCustomer = {
          ...customerData,
          id: offlineOperation.id, // ID temporaneo
          isOffline: true,
          tempId: offlineOperation.id
        };
        
        // Aggiorna cache locale con il nuovo cliente
        cacheCustomers([...cachedCustomers, newCustomer]);
      }
      
      // Se è una modifica, aggiorna la cache locale
      if (operation === 'update') {
        const cachedCustomers = getCachedCustomers();
        const updatedCache = cachedCustomers.map(customer => 
          customer.id === customerId ? { ...customer, ...customerData, isOfflineModified: true } : customer
        );
        cacheCustomers(updatedCache);
      }
      
      // Se è una eliminazione, rimuovi dalla cache locale
      if (operation === 'delete') {
        const cachedCustomers = getCachedCustomers();
        const updatedCache = cachedCustomers.filter(customer => customer.id !== customerId);
        cacheCustomers(updatedCache);
      }
      
      return offlineOperation;
    } catch (error) {
      console.error('Errore salvataggio operazione cliente offline:', error);
      throw new Error('Impossibile salvare l\'operazione cliente offline');
    }
  };

  /**
   * Ottiene tutte le operazioni offline sui clienti
   */
  const getOfflineCustomerOperations = () => {
    try {
      const operations = JSON.parse(localStorage.getItem(OFFLINE_CUSTOMERS_KEY) || '[]');
      return operations.filter(op => !op.synced);
    } catch (error) {
      console.error('Errore lettura operazioni clienti offline:', error);
      return [];
    }
  };

  /**
   * Sincronizza tutte le operazioni offline sui clienti
   */
  const syncOfflineCustomers = async () => {
    if (!isOnline) {
      throw new Error('Connessione non disponibile');
    }

    try {
      const operations = JSON.parse(localStorage.getItem(OFFLINE_CUSTOMERS_KEY) || '[]');
      const pendingOperations = operations.filter(op => !op.synced);

      if (pendingOperations.length === 0) {
        return { success: true, syncedCount: 0, errors: [] };
      }

      let syncedCount = 0;
      let errors = [];
      const updatedOperations = [...operations];

      // Sincronizza le operazioni sequenzialmente
      for (let i = 0; i < pendingOperations.length; i++) {
        const operation = pendingOperations[i];
        const operationIndex = updatedOperations.findIndex(o => o.id === operation.id);
        
        try {
          
          // Incrementa contatore tentativi
          updatedOperations[operationIndex].retryCount = (operation.retryCount || 0) + 1;
          updatedOperations[operationIndex].lastSyncAttempt = Date.now();

          let result;
          
          switch (operation.operation) {
            case 'create':
              result = await ApiService.createCustomer(operation.customerData);
              break;
              
            case 'update':
              result = await ApiService.updateCustomer({
                ...operation.customerData,
                id: operation.customerId
              });
              break;
              
            case 'delete':
              result = await ApiService.deleteCustomer(operation.customerId);
              break;
              
            default:
              throw new Error(`Operazione non supportata: ${operation.operation}`);
          }
          
          // Marca come sincronizzato
          updatedOperations[operationIndex] = {
            ...updatedOperations[operationIndex],
            synced: true,
            syncedAt: Date.now(),
            serverResult: result
          };
          
          syncedCount++;
          
        } catch (syncError) {
          console.error(`❌ Errore sincronizzazione operazione cliente ${operation.id}:`, syncError);
          const errorMsg = syncError.message || 'Errore sconosciuto';
          
          errors.push({
            operationId: operation.id,
            operation: operation.operation,
            error: errorMsg,
            retryCount: updatedOperations[operationIndex].retryCount
          });
          
          // Se troppi tentativi falliti, marca come errore permanente
          if (updatedOperations[operationIndex].retryCount >= 3) {
            updatedOperations[operationIndex].syncError = errorMsg;
            updatedOperations[operationIndex].permanentFailure = true;
            console.error(`💀 Operazione cliente fallita definitivamente: ${operation.id}`);
          }
        }
      }

      // Salva le operazioni aggiornate
      localStorage.setItem(OFFLINE_CUSTOMERS_KEY, JSON.stringify(updatedOperations));
      
      // Ricarica la cache clienti dal server per avere i dati aggiornati
      if (syncedCount > 0) {
        try {
          const freshCustomers = await ApiService.getAllCustomers();
          if (freshCustomers && Array.isArray(freshCustomers)) {
            cacheCustomers(freshCustomers);
          }
        } catch (refreshError) {
          console.warn('⚠️ Impossibile aggiornare cache clienti:', refreshError);
        }
      }
      
      const result = {
        success: errors.length < pendingOperations.length,
        syncedCount,
        totalCount: pendingOperations.length,
        errors
      };
      
      return result;
      
    } catch (error) {
      console.error('💥 Errore generale sync operazioni clienti:', error);
      throw error;
    }
  };

  /**
   * Pulisce le operazioni clienti già sincronizzate
   */
  const clearSyncedCustomerOperations = () => {
    try {
      const operations = JSON.parse(localStorage.getItem(OFFLINE_CUSTOMERS_KEY) || '[]');
      const pendingOperations = operations.filter(op => !op.synced);
      
      localStorage.setItem(OFFLINE_CUSTOMERS_KEY, JSON.stringify(pendingOperations));
      
      return pendingOperations.length;
    } catch (error) {
      console.error('Errore pulizia operazioni clienti sincronizzate:', error);
      return 0;
    }
  };

  return {
    isOnline,
    offlineOrders,
    pendingSyncCount,
    isSyncing,             // Per debug e UI feedback
    loadOfflineOrders,      // Esposta per uso esterno se necessario
    saveOfflineOrder,
    clearSyncedOrders,
    syncPendingOrders,
    cacheCustomers,
    getCachedCustomers,
    cacheProducts,
    getCachedProducts,
    shouldUpdateCache,
    // Nuove funzioni per gestione offline clienti
    saveOfflineCustomerOperation,
    getOfflineCustomerOperations,
    syncOfflineCustomers,
    clearSyncedCustomerOperations
  };
};